/**
 * Bank Transaction Hooks (35-kör Fázis-1 — Pénzügy bank-import)
 *
 * useState-alapú adatkezelés (a kgc-web hook-konvenció, lásd use-inventory).
 * A típusok a `@kgc/api-contract`-ból jönnek (NINCS FE-kézi-zod).
 */

import type {
  BankTransaction,
  BankTransactionListQuery,
  BankTransactionMatch,
  CreateBankTransaction,
  MatchBankTransaction,
} from '@kgc/api-contract';
import { useCallback, useEffect, useState } from 'react';

import {
  createBankTransaction,
  getBankTransactions,
  matchBankTransaction,
} from '@/api/bank-transactions';

// ============================================
// LIST HOOK
// ============================================

interface UseBankTransactionsResult {
  transactions: BankTransaction[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Banki tétel lista betöltése pagination + matchStatus/direction szűrővel.
 */
export function useBankTransactions(
  query: Partial<BankTransactionListQuery> = {}
): UseBankTransactionsResult {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getBankTransactions(query);
      setTransactions(response.data);
      setTotal(response.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült a banki tételek betöltése';
      setError(message);
    } finally {
      setIsLoading(false);
    }
    // a query primitív mezőire szűkített dep-lista (a use-inventory mintára)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.matchStatus, query.direction]);

  useEffect(() => {
    void fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, total, isLoading, error, refetch: fetchTransactions };
}

// ============================================
// CREATE HOOK (kézi rögzítés)
// ============================================

interface UseCreateBankTransactionResult {
  create: (dto: CreateBankTransaction) => Promise<BankTransaction>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Kézi banki tétel rögzítés (POST /bank-transactions).
 */
export function useCreateBankTransaction(): UseCreateBankTransactionResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(async (dto: CreateBankTransaction): Promise<BankTransaction> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await createBankTransaction(dto);
      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Nem sikerült a banki tétel rögzítése';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error };
}

// ============================================
// MATCH HOOK (Fázis 2 — money-write párosítás)
// ============================================

interface UseMatchBankTransactionResult {
  match: (id: string, dto: MatchBankTransaction) => Promise<BankTransactionMatch>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Bank-tétel ↔ számla párosítás (POST /bank-transactions/:id/match, money-write).
 * Hiba esetén az ApiError-t re-throw-olja → a hívó a `bankMatchErrorToastKey`-jel
 * code-alapú toast-ot ad (a `setError` csak a generikus state-hez).
 */
export function useMatchBankTransaction(): UseMatchBankTransactionResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const match = useCallback(
    async (id: string, dto: MatchBankTransaction): Promise<BankTransactionMatch> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await matchBankTransaction(id, dto);
        return response.data;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Nem sikerült a párosítás';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { match, isLoading, error };
}
